import { infoApiRef } from '@agilelab/plugin-wb-platform';
import { Progress } from '@mainstage/core-components';
import { useApi } from '@mainstage/core-plugin-api';
import { ReactNode, useEffect, useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { LicenseExpiredPage } from './LicenseExpiredPage';
import {
  LICENSE_EXPIRED_EVENT,
  licenseExpiredFlag,
} from './licenseExpiredInterceptor';

export const LicenseGuard = ({ children }: { children: ReactNode }) => {
  const infoApi = useApi(infoApiRef);
  const [expiredViaInterceptor, setExpiredViaInterceptor] = useState(false);

  // Listen for the global license-expired event emitted by the fetch interceptor.
  // This fires synchronously when ANY API call gets a license 402, so it reacts
  // before individual components can display their own error UI.
  useEffect(() => {
    const handler = () => setExpiredViaInterceptor(true);
    window.addEventListener(LICENSE_EXPIRED_EVENT, handler);
    return () => window.removeEventListener(LICENSE_EXPIRED_EVENT, handler);
  }, []);

  // Check via the /info endpoint on initial load only.
  // Ongoing detection is handled by the fetch interceptor above.
  const { loading, value: platformInfo } = useAsync(
    () => infoApi.getInfo({}),
    [infoApi],
  );

  // Check the global flag synchronously — catches the case where the 402
  // arrived before the useEffect listener was registered.
  if (licenseExpiredFlag || expiredViaInterceptor)
    return <LicenseExpiredPage />;

  if (loading) return <Progress />;

  const expirationDate = platformInfo
    ? Date.parse(platformInfo.license.expirationDate)
    : NaN;

  if (!Number.isNaN(expirationDate) && expirationDate < Date.now()) {
    return <LicenseExpiredPage />;
  }

  return <>{children}</>;
};
